import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, Map as MapIcon, List, Briefcase, MapPin, Clock, Image } from "lucide-react";
import { api, ago } from "../lib/api";
import { useAuth, catName } from "../context/AuthContext";
import { useSeo, EmptyState, Skeleton } from "../components/kit";
import { ServiceInput, LocationInput } from "../components/Inputs";
import { UrgentBadge, jobLink } from "../components/Urgent";
import JobsMap from "../components/JobsMap";
import { SaveSearchButton, MyJobAlerts } from "../components/JobAlerts";

function JobResultCard({ j, active, onHover }) {
  const { user, cats } = useAuth();
  return (
    <Link to={jobLink(user, j.id)} onMouseEnter={() => onHover(j.id)} id={`job-${j.id}`}
      className={`card-hover p-5 flex flex-col ${j.urgent ? "border-l-4 border-l-rose-500" : ""} ${active ? "ring-2 ring-[#FF8166]" : ""}`} data-testid={`job-result-${j.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-[#FF8166] uppercase tracking-wide">{catName(cats, j.category)}</span>
        <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={11} />{ago(j.created_at)}</span>
      </div>
      {j.urgent && <div className="mt-2"><UrgentBadge until={j.urgent_until} /></div>}
      <h3 className="font-bold text-slate-900 mt-2 line-clamp-2">{j.title}</h3>
      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{j.description}</p>
      <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
        <span className="flex items-center gap-1"><MapPin size={11} />{j.town || j.postcode_area} {j.postcode_area}{j.distance != null && ` · ${j.distance} mi`}</span>
        <span className="font-medium text-slate-700">{j.budget_label}</span><span>{j.application_count} applicants</span>
        {j.has_media && <span className="flex items-center gap-1"><Image size={11} />Photos</span>}
      </div>
      <span className="mt-3 text-sm font-semibold text-[#FF8166]">{user?.role === "provider" ? "View & quote →" : "Log in to quote →"}</span>
    </Link>
  );
}

export default function SearchJobs() {
  const [sp, setSp] = useSearchParams();
  const { user, cats } = useAuth();
  const [q, setQ] = useState(sp.get("q") || "");
  const [loc, setLoc] = useState(sp.get("location") || "");
  const [f, setF] = useState({ category: sp.get("category") || "", radius: 25, urgent: sp.get("urgent") === "1", budget: "", posted_days: 0, sort: "newest" });
  const [data, setData] = useState(null);
  const [bounds, setBounds] = useState("");
  const [active, setActive] = useState(null);
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState(false);
  const [alertsV, setAlertsV] = useState(0);
  const [saved, setSaved] = useState({ q: "", location: "" });
  useSeo(`Search jobs${loc ? ` in ${loc}` : ""}`, "Browse home-service jobs posted by local homeowners and send your quote on MyHomelyHub.");

  const run = useCallback(async (b = "") => {
    const { data } = await api.get("/search/jobs", { params: { q, location: loc, ...f, bounds: b } });
    setData(data);
    setSaved({ q, location: b ? "" : loc });
  }, [q, loc, f]);
  useEffect(() => { run(); }, [f]); // eslint-disable-line

  const submit = (e) => { e.preventDefault(); setBounds(""); setSp({ q, location: loc, ...(f.category && { category: f.category }) }); run(); };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const pick = (id) => { setActive(id); document.getElementById(`job-${id}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" }); };
  const results = data?.results || [];
  const pins = results.map((j) => ({ id: j.id, lat: j.lat, lng: j.lng, business_name: j.title, town: j.town, postcode_area: j.postcode_area, distance: j.distance, href: jobLink(user, j.id) }));
  const first = results[0];
  const center = data?.center ? [data.center.lat, data.center.lng] : first ? [first.lat, first.lng] : [51.5719, -0.777];

  return (
    <div className="container-x py-6" data-testid="search-jobs-page">
      <div className="mb-4"><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Search jobs</h1>
        <p className="text-sm text-slate-600 mt-1">{user?.role === "provider" ? "Open any job to see the details and send your quote." : "Jobs posted by local homeowners. Professionals log in to see full details and quote."}</p></div>
      <form onSubmit={submit} className="card p-3 grid gap-2 md:grid-cols-[1fr_1fr_auto_auto]" data-testid="jobs-search-form">
        <ServiceInput value={q} onChange={setQ} onPick={(c) => c && set("category", c)} testid="jobs-search-service-input" />
        <LocationInput value={loc} onChange={setLoc} testid="jobs-search-location-input" />
        <button type="button" onClick={() => setFilters(!filters)} className="btn-ghost" data-testid="jobs-filters-toggle"><SlidersHorizontal size={16} />Filters</button>
        <button className="btn-coral" data-testid="jobs-search-submit-btn">Search</button>
      </form>
      {filters && (
        <div className="card p-4 mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm fade-up" data-testid="jobs-filters">
          <select className="input" value={f.category} onChange={(e) => set("category", e.target.value)} aria-label="Service" data-testid="jobs-filter-category">
            <option value="">All services</option>{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select>
          <select className="input" value={f.radius} onChange={(e) => set("radius", e.target.value)} aria-label="Distance" data-testid="jobs-filter-radius">
            {[5, 10, 15, 25, 50, 100].map((r) => <option key={r} value={r}>Within {r} miles</option>)}</select>
          <select className="input" value={f.budget} onChange={(e) => set("budget", e.target.value)} aria-label="Budget" data-testid="jobs-filter-budget">
            <option value="">Any budget</option>{(data?.budgets || []).map((b) => <option key={b}>{b}</option>)}</select>
          <select className="input" value={f.posted_days} onChange={(e) => set("posted_days", +e.target.value)} aria-label="Posted" data-testid="jobs-filter-posted">
            <option value={0}>Any time</option><option value={1}>Last 24 hours</option><option value={3}>Last 3 days</option><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option></select>
          <select className="input" value={f.sort} onChange={(e) => set("sort", e.target.value)} aria-label="Sort" data-testid="jobs-filter-sort">
            <option value="newest">Newest first</option><option value="nearest">Nearest</option><option value="urgent">Urgent first</option></select>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.urgent} onChange={(e) => set("urgent", e.target.checked)} data-testid="jobs-filter-urgent" />Urgent only</label>
        </div>
      )}
      <MyJobAlerts version={alertsV} />
      <div className="flex flex-wrap items-center justify-between gap-3 mt-5 mb-3">
        <p className="text-sm text-slate-600" data-testid="jobs-result-count">{data ? `${results.length} open job${results.length === 1 ? "" : "s"}${data.center?.label ? ` near ${data.center.label}` : ""}` : "Searching…"}</p>
        <div className="flex items-center gap-2">
          <SaveSearchButton search={{ ...saved, category: f.category, radius: +f.radius, urgent: f.urgent, budget: f.budget }} onSaved={() => setAlertsV((v) => v + 1)} />
        <div className="lg:hidden inline-flex p-1 bg-slate-100 rounded-lg">
          <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-md text-xs ${view === "list" ? "bg-white shadow" : ""}`} data-testid="jobs-view-list-btn"><List size={14} /></button>
          <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-md text-xs ${view === "map" ? "bg-white shadow" : ""}`} data-testid="jobs-view-map-btn"><MapIcon size={14} /></button>
        </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5">
        <div className={`space-y-4 lg:max-h-[calc(100vh-14rem)] lg:overflow-auto lg:pr-2 ${view === "map" ? "hidden lg:block" : ""}`} data-testid="jobs-results">
          {!data && [1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
          {data && !results.length && <EmptyState icon={Briefcase} title="No open jobs found here" text="Try a wider distance, another service or a nearby town." testid="jobs-empty" />}
          {results.map((j) => <JobResultCard key={j.id} j={j} active={active === j.id} onHover={setActive} />)}
        </div>
        <div className={`lg:sticky lg:top-24 h-[60vh] lg:h-[calc(100vh-14rem)] ${view === "list" ? "hidden lg:block" : ""}`}>
          <div className="relative h-full">
            <JobsMap items={pins} center={center} zoom={data?.center ? 10 : 8} activeId={active} onSelect={pick}
              onMove={setBounds} />
            {bounds && <button onClick={() => run(bounds)} className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] btn-navy btn-sm shadow-lg" data-testid="jobs-search-area-btn">Search this area</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
